/**
 * 🚀 DYNAMIC CONTAINER SECTION 2025
 * 
 * Ein dynamischer Container innerhalb des HeroHeaders
 * - Ersetzt die fest hardcoded hero-content-wrapper
 * - Kann Text, Images, Mixed Content enthalten
 * - Hat eigene Toolbar zum Editieren
 */

import React, { useState, useEffect, useRef } from 'react';
import HeroContentElement from './HeroContentElement';
import ContainerEditControls from './ContainerEditControls';
import DeleteContainerModal from './DeleteContainerModal';
import AddElementMenu from './AddElementMenu';
import { applyGridPreset } from './gridPresets';
import './DynamicContainerSection.css';

function DynamicContainerSection({
    container,
    containerIndex,
    totalContainers,
    isEditMode,
    isMobileLayout,
    onUpdate,
    onDelete,
    onStyleUpdate,
    isToolbarVisible,
    onToolbarToggle,
    colors,
    colorAssignments,
    fonts,
    availableFonts,
    layoutConfig,
    currentlyEditingElementId,
    onEditingChange,
    containerRef,
    defaultHeroImage
}) {
    // State für Empty State Add Menu
    const [showEmptyStateMenu, setShowEmptyStateMenu] = useState(false);
    const emptyStateMenuRef = useRef(null);
    
    // State für Delete Modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // Close menu on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emptyStateMenuRef.current && !emptyStateMenuRef.current.contains(event.target)) {
                setShowEmptyStateMenu(false);
            }
        };

        if (showEmptyStateMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showEmptyStateMenu]);
    
    // Content Elements sortieren
    const contentElements = container.contentElements 
        ? [...container.contentElements]
            .filter(el => el.visible !== false)
            .sort((a, b) => a.order - b.order)
        : [];
    
    // Element Management Functions
    const handleElementUpdate = (updatedElement) => {
        console.log('🔄 [DynamicContainerSection] handleElementUpdate called:', {
            updatedElementId: updatedElement.id,
            updatedElement,
            containerId: container.id,
            hasOnUpdate: !!onUpdate
        });
        
        if (!onUpdate) {
            console.error('❌ [DynamicContainerSection] onUpdate is undefined!');
            return;
        }
        
        const newElements = container.contentElements.map(el => 
            el.id === updatedElement.id ? updatedElement : el
        );
        
        const updatedContainer = { ...container, contentElements: newElements };
        console.log('✅ [DynamicContainerSection] Calling parent onUpdate with updatedContainer:', updatedContainer);
        onUpdate(updatedContainer);
        console.log('✅ [DynamicContainerSection] Parent onUpdate called successfully');
    };

    const handleElementDelete = (elementId) => {
        const newElements = container.contentElements.filter(el => el.id !== elementId);
        onUpdate({ ...container, contentElements: newElements });
    };

    const handleElementMoveUp = (elementId) => {
        const currentIndex = contentElements.findIndex(el => el.id === elementId);
        if (currentIndex <= 0) return;
        
        const newElements = [...container.contentElements];
        const currentElement = newElements.find(el => el.id === elementId);
        const previousElement = newElements.find(el => el.id === contentElements[currentIndex - 1].id);
        
        if (currentElement && previousElement) {
            const tempOrder = currentElement.order;
            currentElement.order = previousElement.order;
            previousElement.order = tempOrder;
        }
        
        onUpdate({ ...container, contentElements: newElements });
    };

    const handleElementMoveDown = (elementId) => {
        const currentIndex = contentElements.findIndex(el => el.id === elementId);
        if (currentIndex >= contentElements.length - 1) return;
        
        const newElements = [...container.contentElements];
        const currentElement = newElements.find(el => el.id === elementId);
        const nextElement = newElements.find(el => el.id === contentElements[currentIndex + 1].id);
        
        if (currentElement && nextElement) {
            const tempOrder = currentElement.order;
            currentElement.order = nextElement.order;
            nextElement.order = tempOrder;
        }
        
        onUpdate({ ...container, contentElements: newElements });
    };
    
    // ✅ Duplicate Element (inkl. Grid mit allen Cells)
    const handleElementDuplicate = (elementId) => {
        const currentIndex = contentElements.findIndex(el => el.id === elementId);
        if (currentIndex === -1) return;
        
        const elementToDuplicate = contentElements[currentIndex];
        
        // ✅ Deep clone des Elements mit neuen IDs
        const clonedElement = {
            ...elementToDuplicate,
            id: `${elementToDuplicate.type}-${Date.now()}`,
            order: currentIndex + 1, // Direkt nach dem Original
            // ✅ Spezial-Handling für Grid-Elemente (mit Cells)
            ...(elementToDuplicate.type === 'grid' && {
                children: (elementToDuplicate.children || []).map((cell, idx) => ({
                    ...cell,
                    id: `cell-${Date.now()}-${idx}`,
                    cellIndex: idx,
                    order: idx,
                    children: (cell.children || []).map((child, childIdx) => ({
                        ...child,
                        id: `${child.type}-${Date.now()}-${idx}-${childIdx}`,
                        order: childIdx
                    }))
                }))
            })
        };
        
        // ✅ Füge geklontes Element nach dem Original ein
        const newElements = [
            ...container.contentElements.slice(0, currentIndex + 1),
            clonedElement,
            ...container.contentElements.slice(currentIndex + 1).map(el => ({
                ...el,
                order: el.order + 1 // Alle nachfolgenden Elemente um 1 erhöhen
            }))
        ];
        
        onUpdate({ ...container, contentElements: newElements });
    };
    
    // ✅ NEW: Add grid preset to container
    const handleAddGridPreset = (presetId) => {
        const currentElements = container.contentElements || [];
        const presetGrid = applyGridPreset(presetId, currentElements.length, colors, fonts, colorAssignments); // ✅ Colors, Fonts & ColorAssignments durchreichen!
        
        if (!presetGrid) {
            console.error('Preset not found:', presetId);
            return;
        }
        
        console.log('🎨 Adding Grid Preset with brand colors & assignments:', { presetId, colors, colorAssignments, presetGrid });
        
        const updatedElements = [...currentElements, presetGrid];
        onUpdate({ ...container, contentElements: updatedElements });
        setShowEmptyStateMenu(false);
    };

    const handleElementAdd = (type, elementType, position, referenceIndex) => {
        const currentElements = container.contentElements || [];
        const newOrder = position === 'before' ? referenceIndex : referenceIndex + 1;
        
        // Default Styling basierend auf elementType
        const getDefaultTextStyling = (elType) => {
            const defaults = {
                'h1': { fontSize: 48, fontSizeMobile: 36, fontWeight: '700', fontWeightMobile: '700', lineHeight: 1.2, lineHeightMobile: 1.2, maxWidth: 800, maxWidthMobile: 700, marginBottom: 0, marginBottomMobile: 0, placeholder: 'Enter H1 heading...', content: 'New Heading' },
                'h2': { fontSize: 36, fontSizeMobile: 28, fontWeight: '600', fontWeightMobile: '600', lineHeight: 1.3, lineHeightMobile: 1.3, maxWidth: 700, maxWidthMobile: 600, marginBottom: 0, marginBottomMobile: 0, placeholder: 'Enter H2 heading...', content: 'New Subheading' },
                'h3': { fontSize: 24, fontSizeMobile: 20, fontWeight: '600', fontWeightMobile: '600', lineHeight: 1.4, lineHeightMobile: 1.4, maxWidth: 650, maxWidthMobile: 550, marginBottom: 0, marginBottomMobile: 0, placeholder: 'Enter H3 heading...', content: 'New Subtitle' },
                'p': { fontSize: 16, fontSizeMobile: 14, fontWeight: '400', fontWeightMobile: '400', lineHeight: 1.6, lineHeightMobile: 1.6, maxWidth: 600, maxWidthMobile: 500, marginBottom: 0, marginBottomMobile: 0, placeholder: 'Enter paragraph...', content: 'New paragraph text...' },
                'custom': { fontSize: 16, fontSizeMobile: 14, fontWeight: '400', fontWeightMobile: '400', lineHeight: 1.6, lineHeightMobile: 1.6, maxWidth: 600, maxWidthMobile: 500, marginBottom: 0, marginBottomMobile: 0, placeholder: 'Enter text...', content: 'New text element...' }
            };
            return defaults[elType] || defaults['custom'];
        };
        
        const newElement = {
            id: `${type}-${Date.now()}`,
            type: type,
            order: newOrder,
            visible: true,
            elementType: type === 'button' ? 'button' : elementType,
            
            ...(type === 'text' && {
                ...getDefaultTextStyling(elementType),
                // ✅ FIXED: Nutze Brand Font basierend auf elementType (H1 = Big Font, Rest = Small Font)
                fontFamily: elementType === 'h1' 
                    ? (fonts.globalBigFont || '') 
                    : (fonts.globalSmallFont || ''),
                // ✅ FIXED: Nutze Brand Text Color basierend auf colorAssignments
                color: (() => {
                    if (elementType === 'h1' || elementType === 'h2' || elementType === 'h3') {
                        const colorKey = colorAssignments.primaryText || 'color2';
                        return colors[colorKey] || '#333333';
                    } else {
                        const colorKey = colorAssignments.secondaryText || 'color3';
                        return colors[colorKey] || '#666666';
                    }
                })(),
                letterSpacing: 0,
                letterSpacingMobile: 0,
                textAlign: 'center', // ✅ DEFAULT: Mittig
                textAlignMobile: 'center', // ✅ DEFAULT: Mittig auf Mobile
                marginTop: 0,
                marginRight: 0,
                marginLeft: 0,
                marginTopMobile: 0,
                marginRightMobile: 0,
                marginLeftMobile: 0,
                textEffect: '',
                // ✅ Text Box (Badge/Tag Style) - Default deaktiviert
                textBoxEnabled: false,
                textBoxPaddingHorizontal: 12, // Links/Rechts (breiter)
                textBoxPaddingVertical: 6, // Oben/Unten (höher)
                textBoxBackgroundColor: '#000000', // Schwarz als Default
                textBoxBorderEnabled: false,
                textBoxBorderWidth: 2, // 2px als Default
                textBoxBorderColor: '#ffffff', // Weiß als Default
                textBoxBorderRadius: 8
            }),
            
            ...(type === 'button' && {
                buttonText: 'Click me',
                buttonLink: '',
                buttonTextSize: 18,
                buttonTextSizeMobile: 16,
                buttonTextWeight: '600',
                buttonTextWeightMobile: '600',
                // ✅ FIXED: Nutze Brand Font (Small Font für Buttons)
                buttonFontFamily: fonts.globalSmallFont || '',
                buttonWidth: 200, // ✅ Desktop: 200px
                buttonWidthDesktop: 200, // ✅ Desktop Alternative
                buttonWidthMobile: 100, // ✅ Mobile: 100% (Prozent-Wert)
                buttonMaxWidth: 300,
                buttonMaxWidthMobile: 280,
                buttonHeight: 50,
                buttonHeightMobile: 48,
                buttonBorderRadius: 8,
                buttonBorderRadiusMobile: 8,
                // ✅ FIXED: Nutze Brand Button Colors basierend auf colorAssignments
                buttonColor: colors[colorAssignments.buttons || 'color1'] || '#5C80BC',
                buttonTextColor: colors[colorAssignments.buttonText || 'color5'] || '#FFFFFF',
                buttonAlignment: 'center', // ✅ DEFAULT: Mittig
                buttonAlignmentMobile: 'center', // ✅ DEFAULT: Mittig auf Mobile
                buttonGradientEnabled: false,
                buttonGradientColor1: '#667eea',
                buttonGradientColor2: '#764ba2',
                buttonGradientAnimation: false,
                buttonShadowEnabled: false,
                buttonShadowIntensity: 15,
                buttonShadowDistance: 8,
                buttonShadowOpacity: 25,
                buttonShadowColor: '#000000',
                buttonBorderEnabled: false,
                buttonBorderWidth: 2,
                buttonBorderColor: '#5C80BC',
                buttonArrowEnabled: false,
                buttonArrowDirection: 'right',
                buttonArrowIcon: 'arrow_1', // ✅ NEW: Default Icon
                buttonArrowSize: 24, // ✅ NEW: Default Size
                buttonArrowInvert: false, // ✅ NEW: Default nicht invertiert
                buttonAnimationsEnabled: false,
                buttonAnimationType: 'gentle-pulse',
                buttonAnimationSpeed: 2000,
                marginTop: 0,
                marginBottom: 0,
                marginLeft: 0,
                marginRight: 0,
                marginTopMobile: 0,
                marginBottomMobile: 0,
                marginLeftMobile: 0,
                marginRightMobile: 0,
                buttonEffect: ''
            }),
            
            ...(type === 'divider' && {
                dividerStyle: 'simple-line',
                dividerColor: colors.accentColor || '#5C80BC',
                dividerThickness: 2,
                dividerWidth: 100,
                dividerWidthType: 'percent',
                dividerMaxWidth: 800,
                marginTop: 20,
                marginBottom: 20,
                marginLeft: 0,
                marginRight: 0,
                marginTopMobile: 20,
                marginBottomMobile: 20,
                marginLeftMobile: 0,
                marginRightMobile: 0
            }),
            
            // 🕐 NEW: Countdown Bar Element
            ...(type === 'countdownBar' && {
                text: 'Limited Time Offer!',
                countdownMode: 'monthly', // 'fixed' or 'monthly'
                targetDate: '', // ISO datetime string for fixed mode
                
                // Bar Styling
                barHeight: 100,
                barHeightMobile: 80,
                barBorderRadius: 12,
                barBorderRadiusMobile: 12,
                barPadding: 20,
                barPaddingMobile: 20,
                barBackgroundColor: colors[colorAssignments?.buttons || 'color1'] || '#667eea',
                barGradientEnabled: true,
                barGradientColor1: '#667eea',
                barGradientColor2: '#764ba2',
                barBorderEnabled: false,
                barBorderWidth: 2,
                barBorderColor: '#ffffff',
                barShadowEnabled: true,
                shadowIntensity: 20,
                shadowDistance: 8,
                shadowOpacity: 20,
                
                // Text Styling
                textFontSize: 18,
                textFontSizeMobile: 16,
                textFontWeight: '500',
                textFontWeightMobile: '500',
                textFontFamily: fonts?.globalSmallFont || '',
                textColor: '#ffffff',
                
                // Countdown Styling
                countdownFontSize: 32,
                countdownFontSizeMobile: 28,
                countdownFontWeight: '700',
                countdownFontWeightMobile: '700',
                countdownFontFamily: fonts?.globalBigFont || '',
                countdownColor: '#ffffff',
                
                // Timer Container Styling
                timerPosition: 'right', // 'left', 'center', 'right'
                timerLayout: 'horizontal', // 'horizontal' or 'stacked'
                timerGap: 12,
                timerGapMobile: 10,
                timerContainerPadding: 12,
                timerContainerPaddingMobile: 10,
                timerContainerBorderRadius: 8,
                timerContainerBorderRadiusMobile: 8,
                timerContainerBackgroundColor: 'rgba(255, 255, 255, 0.1)',
                timerContainerBorderEnabled: true,
                timerContainerBorderWidth: 1,
                timerContainerBorderColor: 'rgba(255, 255, 255, 0.2)',
                timerContainerShadowEnabled: false,
                timerShadowIntensity: 10,
                timerShadowDistance: 4,
                timerShadowOpacity: 15,
                
                // Display Format
                showDays: true,
                showHours: true,
                showMinutes: true,
                showSeconds: true,
                labelDisplayMode: 'short', // 'short', 'long', 'none', 'custom'
                // Custom Labels (für deutsche Labels etc.)
                customDayLabel: 'Tage',
                customHourLabel: 'Std',
                customMinuteLabel: 'Min',
                customSecondLabel: 'Sek',
                dayLabel: 'd',
                hourLabel: 'h',
                minuteLabel: 'm',
                secondLabel: 's',
                
                // Margins
                marginTop: 0,
                marginBottom: 0,
                marginLeft: 0,
                marginRight: 0,
                marginTopMobile: 0,
                marginBottomMobile: 0,
                marginLeftMobile: 0,
                marginRightMobile: 0
            }),
            
            ...(type === 'image' && {
                imageUrl: '',
                altText: 'Hero Image',
                width: 300,
                widthMobile: 300,
                height: 'auto',
                heightMobile: 'auto',
                maxWidth: 1200,
                maxWidthMobile: 800,
                widthType: 'pixel',
                heightType: 'auto',
                borderRadius: 0,
                borderRadiusMobile: 0,
                objectFit: 'cover',
                alignment: 'center',
                borderEnabled: false,
                borderWidth: 1,
                borderColor: '#e0e0e0',
                shadowEnabled: false,
                shadowIntensity: 15,
                shadowDistance: 4,
                shadowOpacity: 15,
                padding: 0,
                paddingMobile: 0,
                containerBgColor: '#ffffff',
                marginTop: 0,
                marginBottom: 0,
                marginLeft: 0,
                marginRight: 0,
                marginTopMobile: 0,
                marginBottomMobile: 0,
                marginLeftMobile: 0,
                marginRightMobile: 0
            }),
            
            // 🎥 NEW: Video Element
            ...(type === 'video' && {
                videoUrl: '',
                videoWidth: 100,
                videoWidthMobile: 100,
                videoWidthType: '%',
                videoHeight: 500,
                videoHeightMobile: 400,
                aspectRatio: '16-9',
                videoAlignment: 'center',
                borderRadius: 12,
                borderRadiusMobile: 12,
                borderEnabled: false,
                borderWidth: 2,
                borderColor: '#667eea',
                shadowEnabled: false,
                shadowBlur: 16,
                shadowDistance: 8,
                shadowOpacity: 25,
                marginTop: 20,
                marginBottom: 20,
                marginLeft: 0,
                marginRight: 0,
                marginTopMobile: 20,
                marginBottomMobile: 20,
                marginLeftMobile: 0,
                marginRightMobile: 0
            }),
            
            // 🎨 NEW: Gallery Element (Carousel or Grid Masonry)
            ...(type === 'gallery' && {
                galleryLayout: 'carousel', // Default Desktop: carousel
                galleryLayoutMobile: 'carousel', // Default Mobile: carousel (kann separat geändert werden)
                galleryColumns: 3,
                galleryColumnsMobile: 2,
                galleryGap: 20,
                galleryGapMobile: 15,
                galleryItemHeight: 400,
                galleryItemHeightMobile: 300,
                galleryItemMaxWidth: 500, // Max width per item
                galleryItemMaxWidthMobile: 350,
                galleryItemBorderRadius: 12,
                galleryItemBorderRadiusMobile: 12,
                galleryHoverEffect: 'zoom', // 'zoom' | 'lift' | 'fade' | 'none'
                galleryImageFit: 'cover', // 'cover' | 'contain'
                
                // ✅ Text Display Settings (optimiert für bessere Layouts)
                galleryTextDisplayMode: 'gradient-bottom', // 'gradient-bottom' | 'below-image' | 'overlay-dark' | 'card-below'
                galleryTextBackground: 'rgba(0, 0, 0, 0.85)',
                galleryTextColor: '#ffffff',
                
                // ✅ Text Field Toggles (alle default an)
                galleryShowTitle: true,
                galleryShowSubtitle: true,
                galleryShowDescription: true,
                galleryShowCaption: true,
                
                // ✅ Text Styling (pro Feld) - Responsive
                galleryTitleSize: 18,
                galleryTitleSizeMobile: 16,
                galleryTitleWeight: '700',
                gallerySubtitleSize: 14,
                gallerySubtitleSizeMobile: 12,
                gallerySubtitleWeight: '600',
                galleryCaptionFontSize: 14,
                galleryCaptionFontSizeMobile: 12,
                
                // ✅ Carousel Settings
                galleryShowCarouselNav: true,
                galleryEdgeMargin: 0,
                galleryEdgeMarginMobile: 0,
                
                // General Settings
                galleryLightboxEnabled: true,
                galleryAnimationsEnabled: true,
                galleryAnimationDelay: 100, // ms between each item
                
                marginTop: 20,
                marginBottom: 20,
                marginLeft: 0,
                marginRight: 0,
                marginTopMobile: 20,
                marginBottomMobile: 20,
                marginLeftMobile: 0,
                marginRightMobile: 0,
                
                // Gallery Items Array (ERWEITERT mit mehr Text-Feldern)
                galleryItems: [
                    // {
                    //     id: 'gallery-item-1',
                    //     imageUrl: '',
                    //     title: '',        // ✅ NEU
                    //     subtitle: '',     // ✅ NEU
                    //     description: '',  // ✅ NEU
                    //     caption: '',
                    //     alt: '',
                    //     link: '',
                    //     order: 0,
                    //     aspectRatio: 1.5,
                    //     height: 300
                    // }
                ]
            }),
            
            // ✅ NEW: Grid Layout
            ...(type === 'grid' && {
                columns: elementType === '2-columns' ? 2 : (elementType === '3-columns' ? 3 : 2),
                gap: 20,
                gapMobile: 15,
                columnTemplate: 'equal',
                marginTop: 20,
                marginBottom: 20,
                padding: 0,
                // ✅ Grid Direction & Flow
                gridDirection: 'horizontal',
                gridDirectionMobile: 'horizontal', // ✅ Separate Mobile Direction
                gridFlowMode: 'fixed',
                maxItemsPerRow: 3,
                minItemWidth: 200,
                // ✅ Cell Default Styles (gelten für ALLE Cells, können überschrieben werden)
                cellDefaultBackground: 'transparent',
                cellDefaultPadding: 15,
                cellDefaultPaddingMobile: 12,
                // ✅ Min/Max Width nicht setzen - Grid übernimmt Breite automatisch
                cellDefaultBorderRadius: 8,
                cellDefaultBorderEnabled: false,
                cellDefaultBorderWidth: 1,
                cellDefaultBorderColor: '#e0e0e0',
                cellDefaultBoxShadow: 'none', // ✅ KEIN Shadow beim Erstellen
                cellDefaultFixedHeight: false,
                cellDefaultHeight: 200,
                // ✅ Cell Content Direction (Desktop/Mobile)
                cellDefaultFlexDirection: 'column',
                cellDefaultFlexDirectionMobile: 'column',
                // ✅ Cell Content Alignment
                cellDefaultAlignItems: 'flex-start',
                cellDefaultJustifyContent: 'center',
                // ✅ Cell Content Gap (between elements in cell)
                cellDefaultGap: 12,
                cellDefaultGapMobile: 12,
                // ✅ Auto-create empty cells (verwenden Grid Defaults beim Rendering)
                children: Array.from({ 
                    length: elementType === '2-columns' ? 2 : (elementType === '3-columns' ? 3 : 2) 
                }, (_, i) => ({
                    id: `cell-${Date.now()}-${i}`,
                    type: 'cell',
                    cellIndex: i,
                    order: i,
                    children: [] // ✅ Keine hardcodierten Styles - verwenden Grid Defaults
                }))
            })
        };
        
        const updatedElements = currentElements.map(el => ({
            ...el,
            order: el.order >= newOrder ? el.order + 1 : el.order
        }));
        
        const newElements = [...updatedElements, newElement];
        onUpdate({ ...container, contentElements: newElements });
    };
    
    // Container Styles
    const getContainerStyles = () => {
        const appendPx = (value) => {
            if (typeof value === 'string' && isNaN(value)) return value;
            return `${value}px`;
        };
        
        // ✅ Helper: Convert hex color to rgba with opacity
        const hexToRgba = (hex, opacity = 100) => {
            if (!hex || hex === 'transparent') return hex;
            
            // Remove # if present
            hex = hex.replace('#', '');
            
            // Parse hex to RGB
            let r, g, b;
            if (hex.length === 3) {
                r = parseInt(hex[0] + hex[0], 16);
                g = parseInt(hex[1] + hex[1], 16);
                b = parseInt(hex[2] + hex[2], 16);
            } else {
                r = parseInt(hex.substring(0, 2), 16);
                g = parseInt(hex.substring(2, 4), 16);
                b = parseInt(hex.substring(4, 6), 16);
            }
            
            // Convert opacity (0-100) to alpha (0-1)
            const alpha = opacity / 100;
            
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };
        
        // ✅ Get background color with opacity (responsive: Desktop/Mobile)
        const getBackgroundColor = () => {
            // ✅ Gradient Enabled (responsive)
            const gradientEnabled = isMobileLayout
                ? (container.containerBackgroundGradientEnabledMobile ?? container.containerBackgroundGradientEnabled ?? false)
                : (container.containerBackgroundGradientEnabled ?? false);
            
            if (gradientEnabled) {
                // Gradient (responsive colors)
                const color1 = isMobileLayout
                    ? (container.containerBackgroundGradientColor1Mobile ?? container.containerBackgroundGradientColor1 ?? '#667eea')
                    : (container.containerBackgroundGradientColor1 ?? '#667eea');
                const color2 = isMobileLayout
                    ? (container.containerBackgroundGradientColor2Mobile ?? container.containerBackgroundGradientColor2 ?? '#764ba2')
                    : (container.containerBackgroundGradientColor2 ?? '#764ba2');
                
                // 🎯 Gradient Direction (responsive)
                const direction = isMobileLayout
                    ? (container.containerBackgroundGradientDirectionMobile ?? container.containerBackgroundGradientDirection ?? 'to-bottom-right')
                    : (container.containerBackgroundGradientDirection ?? 'to-bottom-right');
                
                // ✅ Map direction to CSS value
                const directionMap = {
                    'to-bottom': 'to bottom',
                    'to-right': 'to right',
                    'to-top': 'to top',
                    'to-left': 'to left',
                    'to-bottom-right': '135deg',
                    'to-bottom-left': '225deg',
                    'to-top-right': '45deg',
                    'to-top-left': '315deg',
                    // ✅ BACKWARD COMPATIBILITY
                    'horizontal': 'to right',
                    'vertical': 'to bottom',
                    'diagonal-left': '135deg',
                    'diagonal-right': '45deg'
                };
                
                const gradientDirection = directionMap[direction] || '135deg';
                
                return `linear-gradient(${gradientDirection}, ${color1} 0%, ${color2} 100%)`;
            }
            
            // ✅ Background Color (responsive)
            const bgColor = isMobileLayout
                ? (container.containerBackgroundColorMobile ?? container.containerBackgroundColor ?? 'transparent')
                : (container.containerBackgroundColor ?? 'transparent');
            
            if (bgColor === 'transparent') return bgColor;
            
            // ✅ Apply opacity if set (responsive)
            const opacity = isMobileLayout
                ? (container.containerBackgroundColorOpacityMobile ?? container.containerBackgroundColorOpacity ?? 100)
                : (container.containerBackgroundColorOpacity ?? 100);
            
            return hexToRgba(bgColor, opacity);
        };
        
        return {
            paddingTop: isMobileLayout 
                ? appendPx(container.containerPaddingTopMobile ?? container.containerPaddingTop ?? 60)
                : appendPx(container.containerPaddingTop ?? 60),
            paddingRight: isMobileLayout
                ? appendPx(container.containerPaddingRightMobile ?? container.containerPaddingRight ?? 40)
                : appendPx(container.containerPaddingRight ?? 40),
            paddingBottom: isMobileLayout
                ? appendPx(container.containerPaddingBottomMobile ?? container.containerPaddingBottom ?? 60)
                : appendPx(container.containerPaddingBottom ?? 60),
            paddingLeft: isMobileLayout
                ? appendPx(container.containerPaddingLeftMobile ?? container.containerPaddingLeft ?? 40)
                : appendPx(container.containerPaddingLeft ?? 40),
            
            marginTop: isMobileLayout
                ? appendPx(container.containerMarginTopMobile ?? container.containerMarginTop ?? 0)
                : appendPx(container.containerMarginTop ?? 0),
            marginRight: isMobileLayout
                ? appendPx(container.containerMarginRightMobile ?? container.containerMarginRight ?? 0)
                : appendPx(container.containerMarginRight ?? 0),
            marginBottom: isMobileLayout
                ? appendPx(container.containerMarginBottomMobile ?? container.containerMarginBottom ?? 0)
                : appendPx(container.containerMarginBottom ?? 0),
            marginLeft: isMobileLayout
                ? appendPx(container.containerMarginLeftMobile ?? container.containerMarginLeft ?? 0)
                : appendPx(container.containerMarginLeft ?? 0),
            
            // ✅ Background (mit Opacity Support)
            background: getBackgroundColor(),
            
            // ✅ Backdrop Filter Blur (für Glassmorphism-Effekt) - Responsive
            backdropFilter: (() => {
                const blur = isMobileLayout
                    ? (container.containerBackdropFilterBlurMobile ?? container.containerBackdropFilterBlur ?? 0)
                    : (container.containerBackdropFilterBlur ?? 0);
                return blur ? `blur(${blur}px)` : 'none';
            })(),
            WebkitBackdropFilter: (() => {
                const blur = isMobileLayout
                    ? (container.containerBackdropFilterBlurMobile ?? container.containerBackdropFilterBlur ?? 0)
                    : (container.containerBackdropFilterBlur ?? 0);
                return blur ? `blur(${blur}px)` : 'none';
            })(), // Safari Support
            
            display: 'flex',
            flexDirection: 'column',
            justifyContent: container.containerContentAlignmentVertical || 'center',
            alignItems: container.containerContentAlignmentHorizontal || 'center',
            
            borderRadius: isMobileLayout
                ? appendPx(container.containerBorderRadiusMobile ?? container.containerBorderRadius ?? 0)
                : appendPx(container.containerBorderRadius ?? 0),
            border: container.containerBorderEnabled
                ? `${container.containerBorderWidth || 1}px solid ${container.containerBorderColor || '#cccccc'}`
                : 'none',
            boxShadow: container.containerShadowEnabled
                ? `0 ${container.containerShadowDistance || 8}px ${container.containerShadowIntensity || 15}px rgba(0, 0, 0, ${(container.containerShadowOpacity || 25) / 100})`
                : 'none',
            
            width: isMobileLayout
                ? (container.containerWidthMobile !== undefined && container.containerWidthMobile !== null && container.containerWidthMobile !== 'auto' 
                    ? appendPx(container.containerWidthMobile)
                    : '100%')
                : (container.containerWidth !== undefined && container.containerWidth !== null && container.containerWidth !== 'auto'
                    ? appendPx(container.containerWidth)
                    : '100%'),
            height: isMobileLayout
                ? (container.containerHeightMobile !== undefined && container.containerHeightMobile !== null && container.containerHeightMobile !== 'auto'
                    ? appendPx(container.containerHeightMobile)
                    : 'auto')
                : (container.containerHeight !== undefined && container.containerHeight !== null && container.containerHeight !== 'auto'
                    ? appendPx(container.containerHeight)
                    : 'auto'),
            
            position: 'relative',
            overflow: 'hidden',
            zIndex: 10
        };
    };
    
    return (
        <div 
            ref={containerRef}
            className={`dynamic-container-section container-type-${container.type || 'text'} ${isEditMode ? 'edit-mode' : ''}`}
            style={getContainerStyles()}
        >
            {/* ✅ BACKGROUND IMAGE - Separates DIV mit Blur Support */}
            {(() => {
                const backgroundImage = isMobileLayout
                    ? (container.containerBackgroundImageMobile ?? container.containerBackgroundImage)
                    : container.containerBackgroundImage;
                
                if (!backgroundImage) return null;
                
                // ✅ Background Image Settings (responsive)
                const bgImageOpacity = isMobileLayout
                    ? (container.containerBackgroundImageOpacityMobile ?? container.containerBackgroundImageOpacity ?? 100)
                    : (container.containerBackgroundImageOpacity ?? 100);
                
                const bgImageSize = isMobileLayout
                    ? (container.containerBackgroundImageSizeMobile ?? container.containerBackgroundImageSize ?? 'cover')
                    : (container.containerBackgroundImageSize ?? 'cover');
                
                const bgImageScale = isMobileLayout
                    ? (container.containerBackgroundImageScaleMobile ?? container.containerBackgroundImageScale ?? 100)
                    : (container.containerBackgroundImageScale ?? 100);
                
                const bgImagePosition = isMobileLayout
                    ? (container.containerBackgroundImagePositionMobile ?? container.containerBackgroundImagePosition ?? 'center')
                    : (container.containerBackgroundImagePosition ?? 'center');
                
                // ✅ BLUR wird auf das Background Image angewendet (nicht backdrop-filter!)
                const blurValue = isMobileLayout
                    ? (container.containerBackdropFilterBlurMobile ?? container.containerBackdropFilterBlur ?? 0)
                    : (container.containerBackdropFilterBlur ?? 0);
                
                // ✅ Auto-Zoom beim Blurren um weiße Ränder zu vermeiden
                // Je stärker der Blur, desto mehr Zoom (1px blur = ~0.5% mehr zoom)
                const blurZoomFactor = blurValue > 0 ? 1 + (blurValue * 0.005) : 1;
                const finalScale = (bgImageScale / 100) * blurZoomFactor;
                
                return (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 0,
                        overflow: 'hidden', // ✅ Schneidet Blur-Ränder ab
                        borderRadius: 'inherit'
                    }}>
                        <img 
                            src={backgroundImage}
                            alt="Background"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: bgImageSize,
                                objectPosition: bgImagePosition,
                                transform: `scale(${finalScale})`, // ✅ Kombiniert User-Scale + Blur-Zoom
                                opacity: bgImageOpacity / 100,
                                filter: blurValue > 0 ? `blur(${blurValue}px)` : 'none', // ✅ BLUR wird hier angewendet!
                            }}
                        />
                    </div>
                );
            })()}
            
            {/* Container Edit Controls */}
            <ContainerEditControls
                isEditMode={isEditMode}
                isToolbarVisible={isToolbarVisible}
                onEdit={onToolbarToggle}
            />
            
            {/* Delete Container Button (nur wenn mehr als 1 Container) */}
            {isEditMode && totalContainers > 1 && (
                <button
                    className="delete-container-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteModal(true);
                    }}
                    title="Delete Container"
                >
                    <i className="fas fa-trash-alt"></i>
                </button>
            )}
            
            {/* Delete Container Modal */}
            <DeleteContainerModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={() => {
                    onDelete(container.id);
                    setShowDeleteModal(false);
                }}
            />
            
            {/* Content Elements */}
            {contentElements && contentElements.length > 0 ? (
                <div className="container-content-elements" style={{ width: '100%', position: 'relative', zIndex: 2 }}>
                    {contentElements.map((element, index) => (
                        <HeroContentElement
                            key={element.id}
                            element={element}
                            index={index}
                            totalElements={contentElements.length}
                            isEditMode={isEditMode}
                            isMobileLayout={isMobileLayout}
                            onUpdate={handleElementUpdate}
                            onDelete={() => handleElementDelete(element.id)}
                            onDuplicate={() => handleElementDuplicate(element.id)}
                            onMoveUp={() => handleElementMoveUp(element.id)}
                            onMoveDown={() => handleElementMoveDown(element.id)}
                            canMoveUp={index > 0}
                            canMoveDown={index < contentElements.length - 1}
                            onAddBefore={(type, elementType) => {
                                // ✅ Handle Grid Presets
                                if (type === 'gridPreset') {
                                    handleAddGridPreset(elementType);
                                } else {
                                    handleElementAdd(type, elementType, 'before', element.order);
                                }
                            }}
                            onAddAfter={(type, elementType) => {
                                // ✅ Handle Grid Presets
                                if (type === 'gridPreset') {
                                    handleAddGridPreset(elementType);
                                } else {
                                    handleElementAdd(type, elementType, 'after', element.order);
                                }
                            }}
                            colors={colors}
                            colorAssignments={colorAssignments}
                            fonts={fonts}
                            availableFonts={availableFonts}
                            layoutConfig={layoutConfig}
                            currentlyEditingElementId={currentlyEditingElementId}
                            onEditingChange={onEditingChange}
                            depth={0}
                        />
                    ))}
                </div>
            ) : (
                // ✅ FIX: Empty State auch auf Live-Page rendern (für min-height: 200px)
                // Im Edit-Mode: Interaktiv mit Add-Menü
                // Im Live-Mode: Nur Platzhalter für konsistente Höhe
                <div 
                    ref={emptyStateMenuRef}
                    className="container-empty-state"
                    onClick={(e) => {
                        // Nur im Edit-Mode interaktiv
                        if (isEditMode) {
                            // Nur wenn nicht auf Menü geklickt wird
                            if (e.target.classList.contains('container-empty-state') || 
                                e.target.closest('.container-empty-state') && !e.target.closest('.empty-state-add-menu')) {
                                setShowEmptyStateMenu(!showEmptyStateMenu);
                            }
                        }
                    }}
                    style={{ 
                        cursor: isEditMode ? 'pointer' : 'default',
                        position: 'relative',
                        // ✅ Im Live-Mode: Transparent und ohne sichtbaren Content
                        opacity: isEditMode ? 1 : 0,
                        pointerEvents: isEditMode ? 'auto' : 'none'
                    }}
                >
                    {isEditMode && (
                        !showEmptyStateMenu ? (
                            <>
                                <i className="fas fa-plus-circle"></i>
                                <p>Click to add content elements</p>
                            </>
                        ) : (
                            <AddElementMenu 
                                onAdd={(type, elementType) => {
                                    // ✅ Handle Grid Presets differently
                                    if (type === 'gridPreset') {
                                        handleAddGridPreset(elementType);
                                    } else {
                                        handleElementAdd(type, elementType, 'after', -1);
                                    }
                                }}
                                onClose={() => setShowEmptyStateMenu(false)}
                                colors={colors}
                            />
                        )
                    )}
                </div>
            )}
        </div>
    );
}

export default DynamicContainerSection;

